import { MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { DynamicField } from '@cargos/sprintpay-models';
import { FacilityInformationType } from '@cargos/sprintpay-services';
import { environment } from 'src/environments/environment';
import { PaidToFacilitiesId } from '../services/firebase/models/organizations';
import { PaymentCartTypeType } from './payment-type';

export enum BackgroundType {
    Half_Background = 'halfBackground',
    Fill_Background = 'fillBackground',
}

export class Config {
    static allowedDomains = [
        'facility',
        'facilities',
        'https://addressvalidation.googleapis.com',
        'https://bff-token-exchange-729647681538.us-central1.run.app',
        environment.kioskFirebaseFunctions ?? '',
    ];

    static apisWithoutHeader = [
        'https://addressvalidation.googleapis.com',
        'assets/sprintpay/config.json',
        'https://bff-token-exchange-729647681538.us-central1.run.app',
    ];

    static apisWithoutContentType = [];

    static apisWithoutAccessControl = [
        'https://addressvalidation.googleapis.com',
        'https://bff-token-exchange-729647681538.us-central1.run.app',
    ];

    static matFormFieldDefaultOptions?: MatFormFieldDefaultOptions = {
        appearance: 'outline',
        subscriptSizing: 'dynamic',
    };

    constructor(
        // Set a default prefix in AWB inputs.
        public defaultAWBPrefix: string | null = null,
        // URL where charges will be displayed by default. This option can be changed in polar source.
        // This is an internal configuration for developers.
        public chargesURL = '/search-awb/details',
        // Enable or disable the accessories section.
        public enableAccesories = true,
        // Allow the customer to create a subscription if the AWB was not found.
        public allowAWBSubscription = true,
        // Allow the customer to continue with the search of an invalid AWB.
        public allowSearchInvalidAWB = false,
        // Allow the customer to continue to the charges table even if the AWB was not found or charges are empty.
        // If set to <false>, a modal will be displayed.
        public allowContinueWithAWBNotFound = false,
        // Allow the customer to search and add different AWBs to the cart.
        public allowMultipleAWBsPayment = false,
        // Force to request the destination in case the AWB destination is empty, this config enable a dropdown under the search input.
        // This is an internal configuration for developers.
        public forceToRequestDestination = false,
        // Minimum amount allowed to make payments in USD.
        public minimumAmountAllowed = 1,
        // Allow STORAGE payments when ISC is not paid.
        public allowStoragePaymentsWithoutISC = false,
        // Enable payment history for authenticated users.
        public enablePaymentHistory = false,
        // Enable search by flight number.
        public enableSearchByFlightNumber = false,
        // Allow payments when ISC is not paid.
        public allowPaymentsWithoutISCPaid = false,
        // Force edit redirection to charges page.
        // This is an internal configuration for developers.
        public forceToRedirectionToCharges = false,
        // Payment types allowed to be paid without ISC previously paid.
        public paymentTypesNoNeedISC: string[] = [],
        // Enable tabs section in the "Charges" screen.
        public enableTabsCharges = true,
        // Enable nomination for MAWBs and HAWBs.
        public enableNomination = false,
        // This message will be displayed next to the credit card form.
        public creditCardAnnouncement = '',
        // Enable PC as a payment method for guest users. ** Deprecated **
        public enablePC = false,
        // This is a note indicating whether the acceptance of the company policy is required for the minimum amount.
        public acceptCompanyPolicyRequired = false,
        // Payment cart type by default when AWB contains HAWBs.
        // In case the option enableTabsCharges is set to <true>, this option is going to define the default tab.
        public paymentCartType = PaymentCartTypeType.HAWB,
        // Disable the charge button when the status DLV exists for any flight.
        public disableChargeButtonWhenStatusDLV = false,
        // Add the flightNumber and flightDate in the extraData object for every payment request.
        public logicBaseOnFlightNumberAndFlightDate = false,
        // Enable the time selector for the pickup date input.
        public enablePickupTimeOnPickupDate = false,
        // Add facilityCode in payment external data.
        public addFacilityCode = false,
        // Enable AGI Cargo wallet as a payment method for guest users.
        public enableAGIWallet = false,
        // Calculate and display quantity on extra items.
        public calculateQuantityOnExtraItems = true,
        // Display the message "Shipment in transit, unavailable to calculate." when the amount is 0 on the charge table.
        public enableMessageForAmountZero = true,
        // Enable the promo code functionality.
        public enablePromoCode = false,
        // Enable search button below the search input for easier access.
        public enableSearchButtonOutside = false,
        // Enable requesters and unique requester to create an approval flow level.
        public enableRequestorApprovalFlow = false,
        // Enable Echecks as a payment method for guest users.
        public enableEchecksAsGuestPayment = false,
        // Enable optimization of calculation storage.
        // When set to true, it will trigger a new request each time the user changes the pickup date.
        public optimizeCalculationStorage = false,
        // Add firms code in payment external data.
        public addFirmsCodeInExternalData = false,
        // Collect company information by payment.
        public collectCompanyInformationByPayment = false,
        // Enable dynamic fields.
        public enableDynamicFields = false,
        // Allow transfer cart from a GUEST user to a SprintPay account.
        public enableTransferCart = true,
        // Enable FAQ PDF section in the header.
        public enableFAQPDF = false,
        // if set to null there is not a limit of items per cart
        public maxItemsPerCart: number | null = null,
        // Are terms of use handled by an external URL.
        public areTermsOfUseExternal = true,
        // Enable autoPay subscription section for authenticated users.
        public enableAutoPaySubscription = false,
        // Target to Login with SSO
        public loginSSOTarget: string = '_self',
        // Max width enable for header
        public maxWidthHeader = '680px',
        // Enable the edition by charge
        public enableChargeEdition = true,
        // Sets a text for the search awb input.
        public searchAWBLabel = 'Please enter your MAWB',
        // Sets a text for the awb labels.
        public labelAWB: string = 'MAWB',
        // Sets a text for the hawb labels.
        public labelHAWB: string = 'HAWB',
        // Enabled to add payment requests without ISC being paid, since it is 0.
        public isISCZero = false,
        // Allow creating a payment request without providing Shipment information
        public allowPaymentsWithoutShipmentInformation = false,
        // Allowed destinations for the payment request.
        public allowedDestinations: string[] = [],
        // Allow creating a multiple payment request
        public allowMultiplePayments = false,
        // Working schedule form configuration
        public workingScheduleForm: WorkingHours | null = null,
        // allow to enable dark mode in the application.
        public enableDarkMode = true,
        // Allow the system to display breadcrumbs. If set to <false>, the back button will be shown.
        public showBreadcrumbs = true,
        // Allow the system to display the footer.
        public showFooter = false,
        // Shipment form configuration
        public shipmentForm: FeaturesConfigShipmentForm | null = null,
        // Search form configuration for the facility-template.
        public searchForm: FeaturesConfigSearchForm | null = null,
        // Welcome title for the application.
        public welcomeTitle: string = '',
        // Welcome subtitle for the application.
        public welcomeSubtitle: string = '',
        // Footer logo width.
        public footerLogoWidth: string = 'auto',
        // Footer logo height.
        public footerLogoHeight: string = 'auto',
        // Footer company logo.
        public footerCompanyLogo: string = '',
        // Footer sections.
        public footerSections: FooterSection[] | null = null,
        // Supported destinations for the Location. This can be empty, and you can find the information in the token.
        public supportedDestinations: string = '',
        // Enable search componenent on the main page.
        public allowSearchForm: boolean = true,
        // Enable shipment form on the main page.
        public allowShipmentForm: boolean = false,
        // Removes the shadow from the facility-search main page when enabled.
        public removeShadow: boolean = false,
        // Background type used in the facility-template within the application.
        public backgroundType: BackgroundType = BackgroundType.Fill_Background,
        // Collection of images displayed in the facility-template enabled for facility-search flow.
        public backgroundImages: BackgroundImages[] = [],
        // Enable the flight date field in the addPaymentRequest payload
        public enableFlightDateInRequest = false,
        // Enable the image over the title on the main page
        public backgroundImageTitle: string = '',
        // Center background images on facility-template
        public centerBackgroundImages: boolean = false,
        // Show receipt sent notification in payment confirmation
        public showReceiptSent: boolean = true,
        // enable threshold receipt API switch
        public enableThresholdReceiptAPISwitch = false,
        // Enable credit card verification feature.
        public enableCreditCardVerification = false,
        // Alternative logo configurations
        public alternativeLogoLightUrl = '',
        public alternativeLogoDarkUrl = '',
        public alternativeLogoWidth = '',
        public alternativeLogoHeight = '',
        // Main logo configurations
        public mainLogoLightUrl = '',
        public mainLogoDarkUrl = '',
        public mainLogoWidth = '',
        public mainLogoHeight = '',
        // Paid to facility ID options
        public paidToFacilityId = 0,
        public paidToFacilitiesId: PaidToFacilitiesId[] = [],
        public paidToMultipleFacilitiesEnable: boolean = false,
        // Require the customer reference field on the checkout cart. Defaults to true to avoid impacting existing orgs.
        public customerReferenceRequired: boolean = true,
        // Features config
        public features?: FeaturesConfig,
        // Show facility information section in the facility-template.
        public showFacilityInformation?: boolean,
        // Facility information data used in the facility-template.
        public facilityInformation?: FacilityInformationType[],
        // Source of the facility, e.g., SPRINTPAY.
        public facilitySource?: string
    ) {}

    static fromJson({
        defaultAWBPrefix,
        chargesURL,
        enableAccesories,
        allowAWBSubscription,
        allowSearchInvalidAWB,
        allowContinueWithAWBNotFound,
        allowMultipleAWBsPayment,
        forceToRequestDestination,
        minimumAmountAllowed,
        allowStoragePaymentsWithoutISC,
        enablePaymentHistory,
        enableSearchByFlightNumber,
        allowPaymentsWithoutISCPaid,
        forceToRedirectionToCharges,
        paymentTypesNoNeedISC,
        enableTabsCharges,
        enableNomination,
        creditCardAnnouncement,
        enablePC,
        acceptCompanyPolicyRequired,
        paymentCartType,
        disableChargeButtonWhenStatusDLV,
        logicBaseOnFlightNumberAndFlightDate,
        enablePickupTimeOnPickupDate,
        addFacilityCode,
        enableAGIWallet,
        calculateQuantityOnExtraItems,
        enableMessageForAmountZero,
        enablePromoCode,
        enableSearchButtonOutside,
        enableRequestorApprovalFlow,
        enableEchecksAsGuestPayment,
        optimizeCalculationStorage,
        addFirmsCodeInExternalData,
        collectCompanyInformationByPayment,
        enableDynamicFields,
        enableTransferCart,
        enableFAQPDF,
        maxItemsPerCart,
        areTermsOfUseExternal,
        enableAutoPaySubscription,
        loginSSOTarget,
        maxWidthHeader,
        enableChargeEdition,
        searchAWBLabel,
        labelAWB,
        labelHAWB,
        isISCZero,
        allowPaymentsWithoutShipmentInformation,
        allowedDestinations,
        allowMultiplePayments,
        workingScheduleForm,
        enableDarkMode,
        showBreadcrumbs,
        showFooter,
        shipmentForm,
        searchForm,
        welcomeTitle,
        welcomeSubtitle,
        footerLogoWidth,
        footerLogoHeight,
        footerCompanyLogo,
        footerSections,
        supportedDestinations,
        allowSearchForm,
        allowShipmentForm,
        removeShadow,
        backgroundType,
        backgroundImages,
        enableFlightDateInRequest,
        backgroundImageTitle,
        centerBackgroundImages,
        showReceiptSent,
        enableThresholdReceiptAPISwitch,
        enableCreditCardVerification,
        alternativeLogoLightUrl,
        alternativeLogoDarkUrl,
        alternativeLogoWidth,
        alternativeLogoHeight,
        mainLogoLightUrl,
        mainLogoDarkUrl,
        mainLogoWidth,
        mainLogoHeight,
        features,
        paidToFacilityId,
        paidToFacilitiesId,
        paidToMultipleFacilitiesEnable,
        customerReferenceRequired,
        showFacilityInformation,
        facilityInformation,
        facilitySource,
    }: ConfigProperties): Config {
        return new Config(
            defaultAWBPrefix,
            chargesURL,
            enableAccesories,
            allowAWBSubscription,
            allowSearchInvalidAWB,
            allowContinueWithAWBNotFound,
            allowMultipleAWBsPayment,
            forceToRequestDestination,
            minimumAmountAllowed,
            allowStoragePaymentsWithoutISC,
            enablePaymentHistory,
            enableSearchByFlightNumber,
            allowPaymentsWithoutISCPaid,
            forceToRedirectionToCharges,
            paymentTypesNoNeedISC,
            enableTabsCharges,
            enableNomination,
            creditCardAnnouncement,
            enablePC,
            acceptCompanyPolicyRequired,
            paymentCartType,
            disableChargeButtonWhenStatusDLV,
            logicBaseOnFlightNumberAndFlightDate,
            enablePickupTimeOnPickupDate,
            addFacilityCode,
            enableAGIWallet,
            calculateQuantityOnExtraItems,
            enableMessageForAmountZero,
            enablePromoCode,
            enableSearchButtonOutside,
            enableRequestorApprovalFlow,
            enableEchecksAsGuestPayment,
            optimizeCalculationStorage,
            addFirmsCodeInExternalData,
            collectCompanyInformationByPayment,
            enableDynamicFields,
            enableTransferCart,
            enableFAQPDF,
            maxItemsPerCart,
            areTermsOfUseExternal,
            enableAutoPaySubscription,
            loginSSOTarget,
            maxWidthHeader,
            enableChargeEdition,
            searchAWBLabel,
            labelAWB,
            labelHAWB,
            isISCZero,
            allowPaymentsWithoutShipmentInformation,
            allowedDestinations,
            allowMultiplePayments,
            workingScheduleForm,
            enableDarkMode,
            showBreadcrumbs,
            showFooter,
            shipmentForm,
            searchForm,
            welcomeTitle,
            welcomeSubtitle,
            footerLogoWidth,
            footerLogoHeight,
            footerCompanyLogo,
            footerSections,
            supportedDestinations,
            allowSearchForm,
            allowShipmentForm,
            removeShadow,
            backgroundType,
            backgroundImages,
            enableFlightDateInRequest,
            backgroundImageTitle,
            centerBackgroundImages,
            showReceiptSent,
            enableThresholdReceiptAPISwitch,
            enableCreditCardVerification,
            alternativeLogoLightUrl,
            alternativeLogoDarkUrl,
            alternativeLogoWidth,
            alternativeLogoHeight,
            mainLogoLightUrl,
            mainLogoDarkUrl,
            mainLogoWidth,
            mainLogoHeight,
            paidToFacilityId,
            paidToFacilitiesId,
            paidToMultipleFacilitiesEnable,
            customerReferenceRequired,
            features,
            showFacilityInformation,
            facilityInformation,
            facilitySource
        );
    }
}

export interface ConfigProperties {
    defaultAWBPrefix?: string | null;
    chargesURL?: string;
    enableAccesories?: boolean;
    allowAWBSubscription?: boolean;
    allowSearchInvalidAWB?: boolean;
    allowContinueWithAWBNotFound?: boolean;
    allowMultipleAWBsPayment?: boolean;
    forceToRequestDestination?: boolean;
    minimumAmountAllowed?: number;
    allowStoragePaymentsWithoutISC?: boolean;
    enablePaymentHistory?: boolean;
    enableSearchByFlightNumber?: boolean;
    allowPaymentsWithoutISCPaid?: boolean;
    forceToRedirectionToCharges?: boolean;
    paymentTypesNoNeedISC?: string[];
    enableTabsCharges?: boolean;
    enableNomination?: boolean;
    creditCardAnnouncement?: string;
    enablePC?: boolean;
    acceptCompanyPolicyRequired?: boolean;
    paymentCartType?: PaymentCartTypeType;
    disableChargeButtonWhenStatusDLV?: boolean;
    logicBaseOnFlightNumberAndFlightDate?: boolean;
    enablePickupTimeOnPickupDate?: boolean;
    addFacilityCode?: boolean;
    enableAGIWallet?: boolean;
    calculateQuantityOnExtraItems?: boolean;
    enableMessageForAmountZero?: boolean;
    enablePromoCode?: boolean;
    enableSearchButtonOutside?: boolean;
    enableRequestorApprovalFlow?: boolean;
    enableEchecksAsGuestPayment?: boolean;
    optimizeCalculationStorage?: boolean;
    addFirmsCodeInExternalData?: boolean;
    collectCompanyInformationByPayment?: boolean;
    enableDynamicFields?: boolean;
    enableTransferCart?: boolean;
    enableFAQPDF?: boolean;
    maxItemsPerCart?: number | null;
    areTermsOfUseExternal?: boolean;
    enableAutoPaySubscription?: boolean;
    loginSSOTarget?: string;
    maxWidthHeader?: string;
    enableChargeEdition?: boolean;
    searchAWBLabel?: string;
    labelAWB?: string;
    labelHAWB?: string;
    features?: FeaturesConfig;
    isISCZero?: boolean;
    allowPaymentsWithoutShipmentInformation?: boolean;
    allowedDestinations?: string[];
    allowMultiplePayments?: boolean;
    workingScheduleForm?: WorkingHours;
    enableDarkMode?: boolean;
    showBreadcrumbs?: boolean;
    showFooter?: boolean;
    shipmentForm?: FeaturesConfigShipmentForm | null;
    searchForm?: FeaturesConfigSearchForm | null;
    welcomeTitle?: string;
    welcomeSubtitle?: string;
    footerLogoWidth?: string;
    footerLogoHeight?: string;
    footerCompanyLogo?: string;
    footerSections?: FooterSection[] | null;
    supportedDestinations?: string;
    allowSearchForm?: boolean;
    allowShipmentForm?: boolean;
    removeShadow?: boolean;
    backgroundType?: BackgroundType;
    backgroundImages?: BackgroundImages[];
    enableFlightDateInRequest?: boolean;
    backgroundImageTitle?: string;
    centerBackgroundImages?: boolean;
    showReceiptSent?: boolean;
    enableThresholdReceiptAPISwitch?: boolean;
    enableCreditCardVerification?: boolean;
    alternativeLogoLightUrl?: string;
    alternativeLogoDarkUrl?: string;
    alternativeLogoWidth?: string;
    alternativeLogoHeight?: string;
    mainLogoLightUrl?: string;
    mainLogoDarkUrl?: string;
    mainLogoWidth?: string;
    mainLogoHeight?: string;
    paidToFacilityId?: number;
    paidToFacilitiesId?: PaidToFacilitiesId[];
    paidToMultipleFacilitiesEnable?: boolean;
    customerReferenceRequired?: boolean;
    showFacilityInformation?: boolean;
    facilityInformation?: FacilityInformationType[];
    facilitySource?: string;
}

export interface FeaturesConfig {
    enableDarkMode?: boolean;
    shipmentForm?: FeaturesConfigShipmentForm | null;
    searchForm?: FeaturesConfigSearchForm | null;
    showBreadcrumbs?: boolean;
    welcomeTitle?: string;
    welcomeSubtitle?: string;
    showFooter?: boolean;
    footerLogoWidth?: string;
    footerLogoHeight?: string;
    footerCompanyLogo?: string;
    footerSections?: FooterSection[] | null;
    allowShipmentForm?: boolean;
    allowSearchForm?: boolean;
    headerConfig?: HeaderConfig;
    typeFlow?: FeaturesFlowType;
    showFacilityInformation?: boolean;
    facilityInformation?: FacilityInformationType[];
}

export interface FeaturesConfigShipmentForm {
    hawb?: {
        label?: string;
        placeholder?: string;
        order?: number;
        enabled?: boolean;
    };
    awb?: {
        label?: string;
        placeholder?: string;
        order?: number;
        enabled?: boolean;
    };
    amount?: {
        label?: string;
        placeholder?: string;
        order?: number;
        enabled?: boolean;
    };
    extraField1?: {
        label?: string;
        placeholder?: string;
        order?: number;
        enabled?: boolean;
    };
    extraField2?: {
        label?: string;
        placeholder?: string;
        order?: number;
        enabled?: boolean;
    };
    dynamicFields?: DynamicField[];
    workingScheduleForm?: WorkingHours;
    backgroundImages?: BackgroundImages[];
    backgroundColor?: string;
}
export interface FeaturesConfigSearchForm {
    dynamicFields?: DynamicField[];
    logoTitle?: string;
    supportedDestinations?: string;
    removeShadow?: boolean;
    backgroundType?: BackgroundType;
    backgroundImages?: BackgroundImages[];
    backgroundColor?: string;
    facilitySource?: string;
}

export interface BackgroundImages {
    name?: string;
}
export interface WorkingHours {
    timeZone?: string;
    ranges?: WorkingHoursRange[];
}

export interface WorkingHoursRange {
    start?: string;
    end?: string;
    daysRange?: DaysRange;
}

export interface DaysRange {
    from?: string;
    to?: string;
}

export interface OperatingHour {
    days: string;
    schedule: string;
}

export interface HeaderConfig {
    isGradientEnabled?: boolean;
    headerTextColor?: string;
    headerTextActiveColor?: boolean;
    headerBackgroundColor?: string;
    headerBackgroundColor30?: string;
    headerBackgroundColor62?: string;
    headerBackgroundColor100?: string;
}

export enum FeaturesFlowType {
    SHIPMENT_FORM = 'shipmentForm',
    SEARCH_FORM = 'searchForm',
}

export interface FooterSection {
    title?: string;
    elements?: FooterElements[];
}

export interface FooterElements {
    type?: string;
    message?: string;
}
